import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import 'cropperjs/dist/cropper.css';
import React, {useEffect, useRef, useState} from 'react';
import {Cropper} from 'react-cropper';

import {TMediaEntry} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {useRecoilValue} from 'recoil';
import {configState, protocolState} from '../../../recoil/ConfigRecoil';
import {IconButton} from '../../Button';
import {uploadAssetForDocument} from '../../views/Competitions/CompetitionUploadUtils';
import {MediaView} from '../MediaView';
import {SocialEmbed} from '../SocialEmbed';

type IMediaWithoutCaption = Omit<TMediaEntry, 'caption'>;

const IMAGE_MIME_TYPES = 'image/png,image/jpg,image/jpeg';
const VIDEO_MIME_TYPES = 'video/mp4,video/x-m4v,video/*';

interface IProps {
  value: TMediaEntry;
  mediaType?: 'image' | 'video';
  removable?: boolean;
  disabled: boolean;
  dimensions: [width: number, height: number];
  selectedFile?: File;
  fillTransparent?: boolean;
  setIsUpdating: (updating: boolean) => void;
  onUpdate: (media: IMediaWithoutCaption) => void;
  onRemove?: (media: IMediaWithoutCaption) => void;
}

export const EditMedia: React.FC<IProps> = (props) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cropperRef = useRef<HTMLImageElement | null>(null);
  const removable = props.removable ?? false;
  const {value, selectedFile} = props;
  const config = useRecoilValue(configState);
  const protocol = useRecoilValue(protocolState);
  const [over, setOver] = useState(false);
  const [currentValue, setCurrentValue] = useState<IMediaWithoutCaption>(
    props.value,
  );

  const [localImageFile, setLocalImageFile] = useState<File | null>(
    props.selectedFile ?? null,
  );
  const [localImage, setLocalImage] = useState<IMediaWithoutCaption | null>(
    null,
  );
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (selectedFile) {
      _localFileSelected(selectedFile);
    } else {
      _resetImage();
    }
  }, [selectedFile]);

  if (!currentValue) {
    return null;
  }

  const _clearImage = () => {
    setLocalImageFile(null);
    setLocalImage(null);
    setCurrentValue({});
    if (props.onRemove) {
      props.onRemove({});
    }
  };

  const _pickNewImage = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const _onFileInputChanged = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) {
      throw new Error('No selected files!');
    }
    const newFile = event.target.files.item(0);
    _localFileSelected(newFile);
  };

  const _localFileSelected = (file: File | null) => {
    if (file) {
      const fileType = getFileType(file);
      if (fileType) {
        setLocalImageFile(file);
        const fileSource = URL.createObjectURL(file);
        setLocalImage({
          [fileType]: {url: fileSource},
        });
      }
    }
  };

  const _resetImage = () => {
    setLocalImageFile(null);
    setLocalImage(null);
  };

  const _startUpload = async () => {
    if (localImageFile === null) {
      return;
    }
    props.setIsUpdating(true);

    let file: File = localImageFile;
    if (localImage?.image && cropperRef?.current) {
      const imageElement: any = cropperRef?.current;
      const cropper: Cropper = imageElement?.cropper;
      const blob = await getCanvasAsBlob(
        cropper.getCroppedCanvas(
          props.fillTransparent
            ? {width: props.dimensions[0], height: props.dimensions[1]}
            : {
                width: props.dimensions[0],
                height: props.dimensions[1],
                fillColor: '#212121',
              },
        ),
        localImageFile.type,
      );
      if (!blob) {
        return;
      }
      file = new File([blob], localImageFile.name, {type: blob.type});
    }

    const upload = await uploadAssetForDocument(
      {protocol, ...config},
      file,
      _onUploadProgress,
    );
    _resetImage();
    const newValue = {...upload};
    setCurrentValue(newValue);
    props.onUpdate(newValue);
    setUploadProgress(undefined);
  };

  const _onUploadProgress = (progress: number, message: string) => {
    console.log('[UPLOAD]', progress, message);
    setUploadProgress(progress);
  };

  const _onCrop = async () => {
    const imageElement: any = cropperRef?.current;
    const cropper: Cropper = imageElement?.cropper;

    const canvasData = cropper.getCanvasData();
    const cropData = cropper.getCropBoxData();
    const crop = {
      left: cropData.left - canvasData.left,
      top: cropData.top - canvasData.top,
      width: cropData.width,
      height: cropData.height,
    };
    console.log('crop', crop);
  };

  const _hasLocalImage = localImageFile !== null;
  const _hasNoMedia = !currentValue.image && !currentValue.video;

  return (
    <div className="sm:col-span-6">
      <input
        ref={fileRef}
        type="file"
        id="file"
        accept={
          props.mediaType === 'image'
            ? IMAGE_MIME_TYPES
            : props.mediaType === 'video'
              ? VIDEO_MIME_TYPES
              : `${IMAGE_MIME_TYPES},${VIDEO_MIME_TYPES}`
        }
        style={{display: 'none'}}
        onChange={_onFileInputChanged}
      />
      <div
        style={{
          position: 'relative',
          marginTop: 12,
          marginBottom: 20,
          paddingBottom: 0,
          fontSize: 'unset',
          wordWrap: 'unset',
          lineHeight: 'unset',
          maxHeight: '100%',
          maxWidth: '100%',
          aspectRatio: props.dimensions[0] / props.dimensions[1],
          width: localImage?.image
            ? Math.max(props.dimensions[0] + 100, 300)
            : props.dimensions[0],
          borderRadius: 10,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: 'rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
        onMouseEnter={
          !_hasLocalImage || _hasNoMedia ? () => setOver(true) : undefined
        }
        onMouseLeave={
          !_hasLocalImage || _hasNoMedia ? () => setOver(false) : undefined
        }>
        {_hasLocalImage && (
          <div className="relative flex justify-evenly items-center bg-gray-50 py-2">
            <IconButton onClick={_resetImage} disabled={props.disabled}>
              <ArrowPathIcon />
            </IconButton>
            <IconButton onClick={_startUpload} disabled={props.disabled}>
              <ArrowUpTrayIcon />
            </IconButton>
            <div
              style={{
                overflow: 'hidden',
                position: 'absolute',
                bottom: 0,
                width: '100%',
                height: 2,
              }}>
              <div
                className="bg-blue-600"
                style={{
                  width: `${uploadProgress ? uploadProgress * 100 : 0}%`,
                  height: 2,
                }}
              />
            </div>
          </div>
        )}
        {props.value.social && !_hasLocalImage && (
          <SocialEmbed
            style={{marginTop: 20, width: '100%', height: '100%'}}
            embed={props.value.social.embed}
          />
        )}
        {localImage?.image ? (
          <Cropper
            src={localImage?.image?.url}
            dragMode={'move'}
            style={{
              height: '100%',
              width: '100%',
            }}
            autoCropArea={1}
            viewMode={0}
            aspectRatio={props.dimensions[0] / props.dimensions[1]}
            crop={_onCrop}
            ref={cropperRef}
          />
        ) : (
          <MediaView media={localImage ?? currentValue} />
        )}

        {over && !_hasLocalImage && !props.disabled === true && (
          <div
            style={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              top: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.3)',
            }}>
            <div
              style={{
                backgroundColor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 50,
                height: 50,
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              onClick={_hasLocalImage ? undefined : _pickNewImage}>
              <PencilIcon className="text-white h-5 w-5" />
            </div>
            {removable && (
              <div
                style={{
                  backgroundColor: 'black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 50,
                  height: 50,
                  marginLeft: 10,
                  borderRadius: '50%',
                  cursor: 'pointer',
                }}
                onClick={_clearImage}>
                <XMarkIcon className="text-white h-5 w-5" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const getFileType = (file: File) => {
  if (file.type.match('image.*')) {
    return 'image';
  }
  if (file.type.match('video.*')) {
    return 'video';
  }
  console.warn('Unknown file type', file.name);
  return undefined;
};

const getCanvasAsBlob = async (
  canvas: HTMLCanvasElement,
  type: string,
): Promise<Blob | null> =>
  new Promise((response, reject) => {
    canvas.toBlob(
      (b) => {
        response(b);
      },
      type,
      0.88,
    );
  });
